import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  Separator,
  Textarea,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  type MercuryOnboardingData,
  mercuryOnboardingApplicationTypeOptions,
  mercuryOnboardingCitizenshipStatusOptions,
  mercuryOnboardingCompanyStructureOptions,
  mercuryOnboardingDataSchema,
  mercuryOnboardingFormationDocumentTypeOptions,
  mercuryOnboardingIdentificationTypeOptions,
  mercuryOnboardingIsPepOptions,
  mercuryOnboardingJobTitleOptions,
} from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/organisations_/$organisationId_/business-entities_/$businessEntityId_/mercury-onboarding",
)({
  head: () => opsHead("Mercury Onboarding"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
  });
}

function View() {
  const params = useParams({
    from: "/__auth/organisations_/$organisationId_/business-entities_/$businessEntityId_/mercury-onboarding",
  });

  const { data } = useSuspenseQuery(
    graphql(`
      query MercuryOnboardingBusinessEntity($id: ID!) {
        businessEntity(id: $id) {
          id
          name
          address {
            id
            buildingNumber
            streetName
            city
            state
            postcode
            country
          }
        }
      }
    `),
    { variables: { id: params.businessEntityId } },
  );

  const [submitOnboarding] = useMutation(
    graphql(`
      mutation SubmitMercuryOnboarding($input: SubmitMercuryOnboardingInput!) {
        submitMercuryOnboarding(input: $input) {
          signupLink
          onboardingDataId
        }
      }
    `),
  );

  const entityAddress = data.businessEntity?.address;
  const entityAddressValues = {
    address1:
      [entityAddress?.buildingNumber, entityAddress?.streetName]
        .filter(Boolean)
        .join(" ") || undefined,
    city: entityAddress?.city ?? undefined,
    region: entityAddress?.state ?? undefined,
    postalCode: entityAddress?.postcode ?? undefined,
    country: entityAddress?.country ?? undefined,
  };

  const initialValues: MercuryOnboardingData = {
    about: {},
    businessContactDetails: {},
    businessPhysicalAddress: entityAddressValues,
    formationDetails: {
      federalEin: null,
      formationDocumentFileBlob: null,
    },
    beneficialOwners: [{}],
  };

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      const parsed = mercuryOnboardingDataSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: true,
          title: "Validation error",
          description: parsed.error.issues
            .map((i) => `[${i.path.join(".")}] ${i.message}`)
            .join(", "),
        });
        return;
      }

      try {
        const result = await submitOnboarding({
          variables: {
            input: {
              businessEntityId: params.businessEntityId,
              ...parsed.data,
            },
          },
        });
        const signupLink = result.data?.submitMercuryOnboarding?.signupLink;
        toast({
          kind: "success",
          autoHide: false,
          title: "Onboarding submitted",
          description: signupLink
            ? `Signup link: ${signupLink}`
            : "Onboarding data submitted successfully.",
        });
      } catch (err) {
        toast({
          kind: "error",
          autoHide: true,
          title: "Failed to submit",
          description:
            err instanceof Error
              ? err.message
              : "An unexpected error occurred.",
        });
      }
    },
  });

  const businessEntity = data.businessEntity;
  if (!businessEntity) {
    return <Alert kind="error" description="Business entity not found." />;
  }

  const address = businessEntity.address;
  const addressLine = [
    [address?.buildingNumber, address?.streetName].filter(Boolean).join(" "),
    address?.city,
    address?.state,
    address?.postcode,
    address?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <AdminPageContainer testId="admin-mercury-onboarding">
      <AdminPageHeader title="Mercury Onboarding" />

      <Link
        to={`/organisations/${params.organisationId}/business-entities/${params.businessEntityId}`}
        className="mb-6 inline-block text-primary-interactive text-sm underline"
      >
        ← Back to business entity
      </Link>

      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex flex-col gap-8"
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-1">
                <Txt variant="body">{businessEntity.name}</Txt>
                {addressLine && (
                  <Txt variant="description" tone="secondary">
                    {addressLine}
                  </Txt>
                )}
              </div>
            </CardContent>
          </Card>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">Application info</Txt>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <form.AppField name="applicationType">
                    {(field) => (
                      <field.SelectField
                        label="Application type"
                        placeholder="Select type..."
                      >
                        {mercuryOnboardingApplicationTypeOptions.map(
                          ([value, label]) => (
                            <field.SelectItem key={value} value={value}>
                              {label}
                            </field.SelectItem>
                          ),
                        )}
                      </field.SelectField>
                    )}
                  </form.AppField>

                  <form.AppField name="inviteEmail">
                    {(field) => (
                      <field.InputField
                        label="Invite email"
                        type="email"
                        placeholder="applicant@example.com"
                      />
                    )}
                  </form.AppField>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">About the business</Txt>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="mb-4">
                  <Txt variant="description" tone="secondary">
                    Legal business name is automatically populated from the
                    business entity.
                  </Txt>
                </div>
                <div className="flex flex-col gap-4">
                  <form.AppField name="about.description">
                    {(field) => (
                      <field.TextareaField
                        label="Description"
                        placeholder="Describe the business..."
                        rows={3}
                      />
                    )}
                  </form.AppField>

                  <div className="grid grid-cols-2 gap-4">
                    <form.AppField name="about.industry">
                      {(field) => (
                        <field.InputField
                          label="Industry"
                          placeholder="e.g. Software"
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="about.website">
                      {(field) => (
                        <field.InputField
                          label="Website"
                          placeholder="https://example.com"
                        />
                      )}
                    </form.AppField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* string[] — split on change so form value matches schema type directly */}
                    <form.Field name="about.countriesOfOperations">
                      {(field) => (
                        <div className="flex flex-col gap-1">
                          <Label>
                            Countries of operation (comma-separated ISO codes)
                          </Label>
                          <Input
                            value={field.state.value?.join(", ") ?? ""}
                            placeholder="US, AU, GB"
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              field.handleChange(
                                raw
                                  ? raw
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                  : undefined,
                              );
                            }}
                          />
                        </div>
                      )}
                    </form.Field>

                    <form.AppField name="about.countryOfOperation">
                      {(field) => (
                        <field.InputField
                          label="Primary country of operation"
                          placeholder="US"
                        />
                      )}
                    </form.AppField>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">Formation details</Txt>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* string | null — null means not provided */}
                    <form.Field name="formationDetails.federalEin">
                      {(field) => (
                        <div className="flex flex-col gap-1">
                          <Label>Federal EIN</Label>
                          <Input
                            value={field.state.value ?? ""}
                            placeholder="12-3456789 (leave empty if not available)"
                            onChange={(e) =>
                              field.handleChange(e.target.value || null)
                            }
                          />
                        </div>
                      )}
                    </form.Field>

                    <form.AppField name="formationDetails.companyStructure">
                      {(field) => (
                        <field.SelectField
                          label="Company structure"
                          placeholder="Select structure..."
                        >
                          {mercuryOnboardingCompanyStructureOptions.map(
                            ([value, label]) => (
                              <field.SelectItem key={value} value={value}>
                                {label}
                              </field.SelectItem>
                            ),
                          )}
                        </field.SelectField>
                      )}
                    </form.AppField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <form.AppField name="formationDetails.companyOriginCountry">
                      {(field) => (
                        <field.InputField
                          label="Company origin country"
                          placeholder="US"
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="formationDetails.foreignBusinessNumber">
                      {(field) => (
                        <field.InputField
                          label="Foreign business number"
                          placeholder="Optional"
                        />
                      )}
                    </form.AppField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* string | null — null means no document provided */}
                    <form.Field name="formationDetails.formationDocumentFileBlob">
                      {(field) => (
                        <div className="flex flex-col gap-1">
                          <Label>Formation document (upload)</Label>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="text-sm"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                void fileToBase64(file).then((blob) =>
                                  field.handleChange(blob),
                                );
                              } else {
                                field.handleChange(null);
                              }
                            }}
                          />
                          {field.state.value && (
                            <Txt variant="caption" tone="secondary">
                              File loaded
                            </Txt>
                          )}
                        </div>
                      )}
                    </form.Field>

                    <form.AppField name="formationDetails.formationDocumentType">
                      {(field) => (
                        <field.SelectField
                          label="Formation document type"
                          placeholder="Select type..."
                        >
                          {mercuryOnboardingFormationDocumentTypeOptions.map(
                            ([value, label]) => (
                              <field.SelectItem key={value} value={value}>
                                {label}
                              </field.SelectItem>
                            ),
                          )}
                        </field.SelectField>
                      )}
                    </form.AppField>
                  </div>

                  <form.Field name="formationDetails.einDocumentFileBlob">
                    {(field) => (
                      <div className="flex flex-col gap-1">
                        <Label>EIN document (upload)</Label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="text-sm"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              void fileToBase64(file).then((blob) =>
                                field.handleChange(blob),
                              );
                            }
                          }}
                        />
                        {field.state.value && (
                          <Txt variant="caption" tone="secondary">
                            File loaded
                          </Txt>
                        )}
                      </div>
                    )}
                  </form.Field>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <Txt variant="section-title">Business contact details</Txt>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => {
                  const addr1 =
                    [address?.buildingNumber, address?.streetName]
                      .filter(Boolean)
                      .join(" ") || undefined;
                  form.setFieldValue("businessContactDetails.address1", addr1);
                  form.setFieldValue(
                    "businessContactDetails.city",
                    address?.city ?? undefined,
                  );
                  form.setFieldValue(
                    "businessContactDetails.state",
                    address?.state ?? undefined,
                  );
                  form.setFieldValue(
                    "businessContactDetails.postalCode",
                    address?.postcode ?? undefined,
                  );
                  form.setFieldValue(
                    "businessContactDetails.country",
                    address?.country ?? undefined,
                  );
                }}
              >
                Use business entity address
              </Button>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  <form.AppField name="businessContactDetails.phoneNumber">
                    {(field) => (
                      <field.InputField
                        label="Phone number"
                        placeholder="+1 555 000 0000"
                      />
                    )}
                  </form.AppField>

                  <div className="grid grid-cols-2 gap-4">
                    <form.AppField name="businessContactDetails.address1">
                      {(field) => <field.InputField label="Address line 1" />}
                    </form.AppField>
                    <form.AppField name="businessContactDetails.address2">
                      {(field) => <field.InputField label="Address line 2" />}
                    </form.AppField>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <form.AppField name="businessContactDetails.city">
                      {(field) => <field.InputField label="City" />}
                    </form.AppField>
                    <form.AppField name="businessContactDetails.state">
                      {(field) => <field.InputField label="State / region" />}
                    </form.AppField>
                    <form.AppField name="businessContactDetails.postalCode">
                      {(field) => <field.InputField label="Postal code" />}
                    </form.AppField>
                  </div>

                  <form.AppField name="businessContactDetails.country">
                    {(field) => (
                      <field.InputField label="Country" placeholder="US" />
                    )}
                  </form.AppField>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <Txt variant="section-title">Business physical address</Txt>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => {
                  const addr1 =
                    [address?.buildingNumber, address?.streetName]
                      .filter(Boolean)
                      .join(" ") || undefined;
                  form.setFieldValue("businessPhysicalAddress.address1", addr1);
                  form.setFieldValue(
                    "businessPhysicalAddress.city",
                    address?.city ?? undefined,
                  );
                  form.setFieldValue(
                    "businessPhysicalAddress.region",
                    address?.state ?? undefined,
                  );
                  form.setFieldValue(
                    "businessPhysicalAddress.postalCode",
                    address?.postcode ?? undefined,
                  );
                  form.setFieldValue(
                    "businessPhysicalAddress.country",
                    address?.country ?? undefined,
                  );
                }}
              >
                Use business entity address
              </Button>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <form.AppField name="businessPhysicalAddress.address1">
                      {(field) => <field.InputField label="Address line 1" />}
                    </form.AppField>
                    <form.AppField name="businessPhysicalAddress.address2">
                      {(field) => <field.InputField label="Address line 2" />}
                    </form.AppField>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <form.AppField name="businessPhysicalAddress.city">
                      {(field) => <field.InputField label="City" />}
                    </form.AppField>
                    <form.AppField name="businessPhysicalAddress.region">
                      {(field) => <field.InputField label="State / region" />}
                    </form.AppField>
                    <form.AppField name="businessPhysicalAddress.postalCode">
                      {(field) => <field.InputField label="Postal code" />}
                    </form.AppField>
                  </div>

                  <form.AppField name="businessPhysicalAddress.country">
                    {(field) => (
                      <field.InputField label="Country" placeholder="US" />
                    )}
                  </form.AppField>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">Beneficial owners</Txt>
            </div>
            <Card>
              <CardContent className="pt-6">
                <form.Field name="beneficialOwners" mode="array">
                  {(field) => (
                    <div className="flex flex-col gap-6">
                      {field.state.value.map((_, index) => (
                        <div key={index}>
                          {index > 0 && <Separator className="mb-6" />}
                          <div className="mb-4 flex items-center justify-between">
                            <Txt variant="field-label">
                              Beneficial owner {index + 1}
                            </Txt>
                            {field.state.value.length > 1 && (
                              <Button
                                variant="destructive"
                                size="sm"
                                type="button"
                                onClick={() => field.removeValue(index)}
                              >
                                Remove
                              </Button>
                            )}
                          </div>

                          <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].firstName`}
                              >
                                {(f) => <f.InputField label="First name" />}
                              </form.AppField>
                              <form.AppField
                                name={`beneficialOwners[${index}].lastName`}
                              >
                                {(f) => <f.InputField label="Last name" />}
                              </form.AppField>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].email`}
                              >
                                {(f) => (
                                  <f.InputField label="Email" type="email" />
                                )}
                              </form.AppField>
                              <form.AppField
                                name={`beneficialOwners[${index}].phoneNumber`}
                              >
                                {(f) => <f.InputField label="Phone number" />}
                              </form.AppField>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].dateOfBirth`}
                              >
                                {(f) => (
                                  <f.InputField
                                    label="Date of birth (YYYY-MM-DD)"
                                    placeholder="1990-01-31"
                                  />
                                )}
                              </form.AppField>

                              {/* number | undefined — parse on change so form value matches schema */}
                              <form.Field
                                name={`beneficialOwners[${index}].percentOwnership`}
                              >
                                {(f) => (
                                  <div className="flex flex-col gap-1">
                                    <Label>Ownership percentage</Label>
                                    <Input
                                      type="number"
                                      value={f.state.value ?? ""}
                                      placeholder="25.00"
                                      onChange={(e) =>
                                        f.handleChange(
                                          e.target.value
                                            ? Number.parseFloat(e.target.value)
                                            : undefined,
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </form.Field>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].citizenshipStatus`}
                              >
                                {(f) => (
                                  <f.SelectField
                                    label="Citizenship status"
                                    placeholder="Select..."
                                  >
                                    {mercuryOnboardingCitizenshipStatusOptions.map(
                                      ([value, label]) => (
                                        <f.SelectItem key={value} value={value}>
                                          {label}
                                        </f.SelectItem>
                                      ),
                                    )}
                                  </f.SelectField>
                                )}
                              </form.AppField>

                              <form.AppField
                                name={`beneficialOwners[${index}].isPep`}
                              >
                                {(f) => (
                                  <f.SelectField
                                    label="Politically exposed person"
                                    placeholder="Select..."
                                  >
                                    {mercuryOnboardingIsPepOptions.map(
                                      ([value, label]) => (
                                        <f.SelectItem key={value} value={value}>
                                          {label}
                                        </f.SelectItem>
                                      ),
                                    )}
                                  </f.SelectField>
                                )}
                              </form.AppField>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].jobTitle`}
                              >
                                {(f) => (
                                  <f.SelectField
                                    label="Job title"
                                    placeholder="Select..."
                                  >
                                    {mercuryOnboardingJobTitleOptions.map(
                                      ([value, label]) => (
                                        <f.SelectItem key={value} value={value}>
                                          {label}
                                        </f.SelectItem>
                                      ),
                                    )}
                                  </f.SelectField>
                                )}
                              </form.AppField>

                              <form.Subscribe
                                selector={(s) =>
                                  s.values.beneficialOwners[index]?.jobTitle
                                }
                              >
                                {(jobTitle) =>
                                  jobTitle === "Other" ? (
                                    <form.AppField
                                      name={`beneficialOwners[${index}].otherJobTitle`}
                                    >
                                      {(f) => (
                                        <f.InputField label="Other job title" />
                                      )}
                                    </form.AppField>
                                  ) : null
                                }
                              </form.Subscribe>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].address1`}
                              >
                                {(f) => <f.InputField label="Address line 1" />}
                              </form.AppField>
                              <form.AppField
                                name={`beneficialOwners[${index}].address2`}
                              >
                                {(f) => <f.InputField label="Address line 2" />}
                              </form.AppField>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].city`}
                              >
                                {(f) => <f.InputField label="City" />}
                              </form.AppField>
                              <form.AppField
                                name={`beneficialOwners[${index}].region`}
                              >
                                {(f) => <f.InputField label="State / region" />}
                              </form.AppField>
                              <form.AppField
                                name={`beneficialOwners[${index}].postalCode`}
                              >
                                {(f) => <f.InputField label="Postal code" />}
                              </form.AppField>
                            </div>

                            <form.AppField
                              name={`beneficialOwners[${index}].country`}
                            >
                              {(f) => (
                                <f.InputField
                                  label="Country"
                                  placeholder="US"
                                />
                              )}
                            </form.AppField>

                            <div className="grid grid-cols-2 gap-4">
                              <form.AppField
                                name={`beneficialOwners[${index}].identificationType`}
                              >
                                {(f) => (
                                  <f.SelectField
                                    label="Identification type"
                                    placeholder="Select..."
                                  >
                                    {mercuryOnboardingIdentificationTypeOptions.map(
                                      ([value, label]) => (
                                        <f.SelectItem key={value} value={value}>
                                          {label}
                                        </f.SelectItem>
                                      ),
                                    )}
                                  </f.SelectField>
                                )}
                              </form.AppField>

                              <form.Field
                                name={`beneficialOwners[${index}].identificationBlob`}
                              >
                                {(f) => (
                                  <div className="flex flex-col gap-1">
                                    <Label>
                                      Identification document (upload)
                                    </Label>
                                    <input
                                      type="file"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      className="text-sm"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          void fileToBase64(file).then((blob) =>
                                            f.handleChange(blob),
                                          );
                                        }
                                      }}
                                    />
                                    {f.state.value && (
                                      <Txt variant="caption" tone="secondary">
                                        File loaded
                                      </Txt>
                                    )}
                                  </div>
                                )}
                              </form.Field>
                            </div>

                            {/* string[] — split on change so form value matches schema type directly */}
                            <form.Field
                              name={`beneficialOwners[${index}].socialProfileLinks`}
                            >
                              {(f) => (
                                <div className="flex flex-col gap-1">
                                  <Label>
                                    Social profile links (one per line)
                                  </Label>
                                  <Textarea
                                    value={f.state.value?.join("\n") ?? ""}
                                    placeholder="https://linkedin.com/in/..."
                                    rows={2}
                                    onChange={(e) => {
                                      const raw = e.target.value.trim();
                                      f.handleChange(
                                        raw
                                          ? raw
                                              .split("\n")
                                              .map((s) => s.trim())
                                              .filter(Boolean)
                                          : undefined,
                                      );
                                    }}
                                  />
                                </div>
                              )}
                            </form.Field>
                          </div>
                        </div>
                      ))}

                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() =>
                          field.pushValue(
                            {} as MercuryOnboardingData["beneficialOwners"][number],
                          )
                        }
                      >
                        Add beneficial owner
                      </Button>
                    </div>
                  )}
                </form.Field>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end">
            <form.SubmitButton>Submit onboarding data</form.SubmitButton>
          </div>
        </form>
      </form.AppForm>
    </AdminPageContainer>
  );
}
